<template>
  <div class="help-center-header">
    <div class="head-layout container">
      <div v-if="['/support/'].includes($route.path)">
        <h1 class="h2">
          {{ title }}
        </h1>
        <p v-if="$config.platform === 'ds'" class="description">
          Do you have a question regarding the platform and your experience? 365 Data Science is here to support you. Search our articles or choose a topic to find the answers you need quickly.
        </p>
      </div>
      <p v-else class="h2">
        {{ title }}
      </p>
      <form
        action=""
        method="post"
        class="col-8 sm-8"
        @submit.prevent=""
      >
        <input
          id="help-center-search"
          v-model="query"
          type="text"
          name="Search"
          placeholder="What do you need help with?"
          autocomplete="off"
          tabindex="0"
          @keyup.arrow-down.exact.prevent="arrowsPress"
          @keyup.arrow-up.exact.prevent="arrowsPress"
          @keyup="search"
          @focus="search"
          @blur="clearSearch"
        >
        <p
          v-if="!validQuery"
          class="alert"
        >
          * Your entry must be at least 3 characters long!
        </p>
        <button type="button">
          <CloseSearch v-if="query" class="close-search" @click="clearSearch" />
          <Search v-else class="search" />
        </button>

        <ul v-show="results && results.length" class="search-results">
          <li v-for="(result, idx) in results" :key="idx">
            <nuxt-link :to="{ path: `/support/${result.slug}/` }" :class="{ focused: idx === focus }">
              {{ result.title }}
            </nuxt-link>
          </li>
        </ul>
      </form>
    </div>
  </div>
</template>
<script>
import { debounce } from 'lodash-es';
import Search from '../../assets/svg/icon-search.svg?vue-component';
import CloseSearch from '../../assets/svg/icon-close.svg?vue-component';

export default {
  components: { Search, CloseSearch },
  data() {
    return {
      query: this.$route.query.q || '',
      results: [],
      focus: -1,
      validQuery: true,
      title: `${this.$config.platformName} Help Center`,
    };
  },
  mounted() {
    document.getElementById('help-center-search').addEventListener('keyup', (event) => {
      if (event.key === 'Enter') {
        this.redirectToSearchPage();
      }
    });
  },
  methods: {
    redirectToSearchPage() {
      const { query } = this;

      if (!query || query.length < 3) {
        this.validQuery = false;
        return false;
      }
      this.validQuery = true;

      if (this.focus >= 0) { // when click enter and item of dropdown is focused
        const { slug } = this.results[this.focus];

        return this.$router.push(`/support/${slug}/`);
      }

      this.clearSearch();
      return this.$router.push({ path: '/support/search/', query: { q: query } });
    },
    search: debounce(async function delay(e) {
      if (!e) {
        return;
      }

      if (e.code === 'Enter' || e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        return;
      }

      if (this.query.length < 3) {
        this.results = [];
        return;
      }

      this.focus = -1;
      const { articles } = await this.$axios.$get(`/help-center/search?q=${this.query}&limit=5`, { progress: false });
      this.results = articles;
    }, 500),
    clearSearch() {
      setTimeout(() => {
        this.query = '';
        this.focus = -1;
        this.results = [];
      }, 100);
    },
    arrowsPress(e) {
      if (e.code === 'ArrowDown') {
        if (this.focus >= this.results.length - 1) {
          this.focus = 0;
        } else {
          this.focus += 1;
        }
        return;
      }

      if (this.focus === 0) {
        this.focus = this.results.length - 1;
      } else {
        this.focus -= 1;
      }
    },
  },
};
</script>
<style scoped lang="scss">

.help-center-header {
  position: relative;
  display: flex;
  align-content: center;
  align-items: center;
  min-height: 305px;
  color: #FFF;
  background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
  background-color: var(--main-dark-light-1);
  text-align: center;

  .head-layout {
    margin: 60px auto 0;

    .description {
      width: 70%;
      margin: var(--spacing-16) auto;
    }
  }

  h1,
  .h2 {
    margin-bottom: var(--spacing-16);
    display: block;
    font-size: 32px;
  }
}

form {
  margin: 0 auto;
  position: relative;
  border-radius: 5px;
  z-index: 1;

  input {
    width: 100%;
    height: 58px;
    font-size: 16px;
    padding: 12px 12px 12px 52px;
    border: 1px solid var(--main-dark-light-3);
    border-radius: 100px;
    background-color: transparent;
    color: var(--main-light-shade-4);

    &::placeholder {
      color: var(--main-light-shade-4);
      font-size: var(--fs-base);
    }

    &:not(:focus):hover {
      border: 1px solid var(--main-light-shade-4);
      color: var(--main-light-shade-1);

      &::placeholder {
        color: var(--main-light-shade-1);
      }

      + button .search path[stroke] {
        stroke: var(--main-light-shade-4);
      }
    }

    &:focus {
      border: 1px solid var(--accent);
      color: var(--main-light-shade-1);

      &::placeholder {
        color: var(--main-light-shade-1);
      }

      + button .search path {
        stroke: var(--accent);
      }

      + button .close-search path {
        fill: var(--accent);
        stroke: var(--accent);
      }
    }

    & + .alert {
      font-size: 14px;
      text-align: left;
      margin-top: 5px;
      padding-left: var(--spacing-48);
      color: var(--main-red);
    }
  }

  button {
    position: absolute;
    line-height: 58px;
    height: 58px;
    padding-left: var(--spacing-16);
    top: 4px;
    left: 0;
    background-color: transparent;
    border: none;
    cursor: pointer;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;

    .close-search {
      width: 18px;
      height: 18px;

      path[fill] {
        fill: #7C7C7C;
      }

      path[stroke] {
        stroke: #7C7C7C;
      }
    }

    .search path {
      stroke: var(--main-dark-light-3);
    }
  }

  .search-results {
    position: absolute;
    left: 0;
    top: 85%;
    background: var(--main-light);
    border-top: 1px solid var(--accent);
    border-radius: 10px;
    width: 100%;
    text-align: left;
    padding: 0;
    list-style: none;
    box-shadow: 0 16px 20px rgba(0, 0, 0, 0.15);
    z-index: 1;
    overflow: hidden;

    a {
      padding-left: 52px;
      padding-top: 10px;
      padding-bottom: 10px;
      color: var(--true-black);
      font-size: 14px;
      font-weight: 500;
      display: block;
      line-height: 24px;

      &:hover,
      &:focus,
      &.focused {
        background-color: var(--accent-light-1);
        text-decoration: none;
      }

      &:active {
        color: var(--accent);
      }
    }
  }
}

@include bp($bp-laptop) {
  .help-center-header {
    .h2 {
      font-size: 28px;
    }
  }
}

@include bp($bp-tablet) {
  .help-center-header {
    .h2 {
      font-size: 28px;
    }
  }
}

@include bp($bp-mobile) {
  .support-page {
    .head-layout.container {
      margin: var(--spacing-80) 0;
    }

    .help-center-header {
      padding-top: var(--spacing-48);
      margin: 0;
    }
  }
}
</style>
